import React, { useState, useEffect } from 'react';
import { useWallets } from '@privy-io/react-auth';
import { ethers } from 'ethers';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { GAME_CONFIG } from '../lib/game-config';
import { KINGSOMNI_PROFILE_ABI } from '../lib/contract-abi';
import { getPreferredEoaWallet } from '../utils/wallet';
import './UpgradeModal.css';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
}

interface PlayerProfile {
  damageLevel: number;
  healthLevel: number;
  fireRateLevel: number;
  unlockFreeze: boolean;
  freezeLevel: number;
  unlockHeal: boolean;
  healLevel: number;
  unlockDamageBoost: boolean;
  damageBoostLevel: number;
}

const STAT_TYPES = [
  { id: 0, name: 'Tower Damage', desc: 'Increases base tower damage' },
  { id: 1, name: 'Tower Health', desc: 'Increases maximum tower health' },
  { id: 2, name: 'Fire Rate', desc: 'Increases attack speed' },
];

const SKILL_TYPES = [
  { id: 3, name: 'Freeze Skill', desc: 'Freezes all enemies' },
  { id: 4, name: 'Heal Skill', desc: 'Restores tower health' },
  { id: 5, name: 'Damage Boost', desc: 'Doubles damage temporarily' },
];

export function UpgradeModal({ isOpen, onClose, user }: UpgradeModalProps) {
  const { wallets } = useWallets();
  const [profile, setProfile] = useState<PlayerProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [txPending, setTxPending] = useState(false);

  const fetchProfile = async () => {
    const wallet = getPreferredEoaWallet(wallets as any[]);
    const address = wallet?.address || user?.address;
    if (!address) return;
    try {
      setLoading(true);
      
      let provider;
      if (wallet) {
        const ethereumProvider = await wallet.getEthereumProvider();
        provider = new ethers.BrowserProvider(ethereumProvider as any);
      } else {
        provider = new ethers.JsonRpcProvider("https://dream-rpc.somnia.network");
      }
      
      const contract = new ethers.Contract(GAME_CONFIG.CONTRACTS.PROFILE, KINGSOMNI_PROFILE_ABI, provider);
      
      const data = await contract.getPlayerProfile(address);
      setProfile({
        damageLevel: Number(data[0]),
        healthLevel: Number(data[1]),
        fireRateLevel: Number(data[2]),
        unlockFreeze: Boolean(data[3]),
        freezeLevel: Number(data[4]),
        unlockHeal: Boolean(data[5]),
        healLevel: Number(data[6]),
        unlockDamageBoost: Boolean(data[7]),
        damageBoostLevel: Number(data[8])
      });
    } catch (err) {
      console.error("Error fetching profile:", err);
      // Fallback default if not registered/error
      setProfile({
        damageLevel: 0, healthLevel: 0, fireRateLevel: 0,
        unlockFreeze: false, freezeLevel: 0,
        unlockHeal: false, healLevel: 0,
        unlockDamageBoost: false, damageBoostLevel: 0
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && user) {
      fetchProfile();
    }
  }, [isOpen, user]);

  if (!isOpen) return null;

  const handleUpgradeStat = async (statId: number, currentLevel: number) => {
    const wallet = getPreferredEoaWallet(wallets as any[]);
    if (!wallet) {
      toast.error("Connect wallet first!");
      return;
    }

    try {
      setTxPending(true);
      const ethereumProvider = await wallet.getEthereumProvider();
      const provider = new ethers.BrowserProvider(ethereumProvider as any);
      const signer = await provider.getSigner();
      
      const contract = new ethers.Contract(GAME_CONFIG.CONTRACTS.PROFILE, KINGSOMNI_PROFILE_ABI, signer);
      
      const cost = GAME_CONFIG.ECONOMY.BASE_UPGRADE_COST;
      const value = ethers.parseEther(cost.toString());

      const tx = await contract.upgradeStat(statId, { value });
      toast.loading("Transaction pending...", { id: "upgrade-tx" });
      
      await tx.wait();
      toast.success("Upgrade successful!", { id: "upgrade-tx" });
      await fetchProfile(); // Refresh
    } catch (err: any) {
      console.error(err);
      toast.error("Upgrade failed. Make sure you have enough STT.", { id: "upgrade-tx" });
    } finally {
      setTxPending(false);
    }
  };

  const handleUnlockSkill = async (skillId: number) => {
    const wallet = getPreferredEoaWallet(wallets as any[]);
    if (!wallet) return;

    try {
      setTxPending(true);
      const ethereumProvider = await wallet.getEthereumProvider();
      const provider = new ethers.BrowserProvider(ethereumProvider as any);
      const signer = await provider.getSigner();
      
      const contract = new ethers.Contract(GAME_CONFIG.CONTRACTS.PROFILE, KINGSOMNI_PROFILE_ABI, signer);
      const value = ethers.parseEther(GAME_CONFIG.ECONOMY.UNLOCK_SKILL_COST.toString());

      const tx = await contract.unlockSkill(skillId, { value });
      toast.loading("Unlocking skill...", { id: "unlock-tx" });
      
      await tx.wait();
      toast.success("Skill unlocked!", { id: "unlock-tx" });
      await fetchProfile();
    } catch (err: any) {
      console.error(err);
      toast.error("Unlock failed.", { id: "unlock-tx" });
    } finally {
      setTxPending(false);
    }
  };

  const getStatLevel = (id: number) => {
    if (!profile) return 0;
    if (id === 0) return profile.damageLevel;
    if (id === 1) return profile.healthLevel;
    if (id === 2) return profile.fireRateLevel;
    return 0;
  };

  const getSkillInfo = (id: number) => {
    if (!profile) return { unlocked: false, level: 0 };
    if (id === 3) return { unlocked: profile.unlockFreeze, level: profile.freezeLevel };
    if (id === 4) return { unlocked: profile.unlockHeal, level: profile.healLevel };
    if (id === 5) return { unlocked: profile.unlockDamageBoost, level: profile.damageBoostLevel };
    return { unlocked: false, level: 0 };
  };

  return (
    <div className="upgrade-modal-overlay">
      <div className="upgrade-modal-content">
        <button className="upgrade-modal-close" onClick={onClose} disabled={txPending}>
          <X size={24} />
        </button>

        <div className="upgrade-header">
          <h2 className="upgrade-title">Kingsomni Arsenal</h2>
          <p className="upgrade-subtitle">Use your STT to upgrade tower & unlock skills</p>
        </div>

        {loading ? (
          <div className="loading-text">Loading profile data from Somnia...</div>
        ) : (
          <div className="upgrade-body">
            <div className="upgrade-section">
              <h3>Core Upgrades</h3>
              <div className="upgrade-list">
                {STAT_TYPES.map(stat => {
                  const level = getStatLevel(stat.id);
                  const cost = GAME_CONFIG.ECONOMY.BASE_UPGRADE_COST.toFixed(2);
                  return (
                    <div className="upgrade-item" key={stat.id}>
                      <div className="upgrade-info">
                        <span className="upgrade-name">{stat.name}</span>
                        <span className="upgrade-desc">{stat.desc}</span>
                        <span className="upgrade-level">Level {level}</span>
                      </div>
                      <div className="upgrade-action">
                        <span className="upgrade-cost">{cost} STT</span>
                        <button 
                          className="upgrade-button" 
                          onClick={() => handleUpgradeStat(stat.id, level)}
                          disabled={txPending}
                        >
                          Upgrade
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="upgrade-section">
              <h3>Skills</h3>
              <div className="upgrade-list">
                {SKILL_TYPES.map(skill => {
                  const info = getSkillInfo(skill.id);
                  const cost = info.unlocked 
                    ? GAME_CONFIG.ECONOMY.BASE_UPGRADE_COST.toFixed(2)
                    : GAME_CONFIG.ECONOMY.UNLOCK_SKILL_COST.toFixed(2);

                  return (
                    <div className="upgrade-item" key={skill.id}>
                      <div className="upgrade-info">
                        <span className="upgrade-name">{skill.name}</span>
                        <span className="upgrade-desc">{skill.desc}</span>
                        <span className="upgrade-level">
                          {info.unlocked ? `Level ${info.level}` : 'Locked'}
                        </span>
                      </div>
                      <div className="upgrade-action">
                        <span className="upgrade-cost">{cost} STT</span>
                        {info.unlocked ? (
                          <button 
                            className="upgrade-button" 
                            onClick={() => handleUpgradeStat(skill.id, info.level)}
                            disabled={txPending}
                          >
                            Upgrade
                          </button>
                        ) : (
                          <button 
                            className="upgrade-button" 
                            onClick={() => handleUnlockSkill(skill.id)}
                            disabled={txPending}
                          >
                            Unlock
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
